import React, { useState, useEffect } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  MarkerClusterer,
  Circle,
  useJsApiLoader
} from '@react-google-maps/api';
import { Typography, Button, Box, LinearProgress } from '@material-ui/core';
import WarningIcon from '@mui/icons-material/Warning';
import { getDisplayAddress } from '../utils/utils';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const libraries = ["places"];

const RenderCircle = ({ center, radius }) => {
  if (!center || !radius) return null;

  return (
    <Circle
      center={center}
      radius={radius}
      options={{
        fillColor: 'rgba(173, 216, 230, 0.3)',
        strokeColor: 'rgba(0, 0, 255, 0.8)',
        strokeOpacity: 0.8,
        strokeWeight: 4,
      }}
    />
  );
};

const Map = ({
  onMapClick,
  homes,
  setHomes,
  selectedHomes,
  onConfirmClick,
  selectedHome,
  setSelectedHome,
  subjectProperty,
  setSelectedHomes,
  setFinalSelectedHomes,
  center,
  setCenter,
  filters,
  onBoundsChange,
  fetchHomes,
  radius,
  tooManyHomes
}) => {
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [map, setMap] = useState(null);

  const defaultCenter = { lat: 43.75311, lng: -79.38672 };

  const handleIdle = () => {
    if (map) {
      const newBounds = map.getBounds();
      if (newBounds) {
        const boundsObj = {
          ne: { lat: newBounds.getNorthEast().lat(), lng: newBounds.getNorthEast().lng() },
          sw: { lat: newBounds.getSouthWest().lat(), lng: newBounds.getSouthWest().lng() }
        };
        fetchHomes(boundsObj, filters);
        onBoundsChange(boundsObj);
      }
    }
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps',
    googleMapsApiKey: 'AIzaSyDXcuNu9hM04ec2v4lqzrfcrmIKjILZMRU',
    libraries
  });

  const handleMapDragEnd = () => {
    if (map) {
      const bounds = map.getBounds();
      if (bounds) {
        const boundsObj = {
          ne: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
          sw: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() }
        };
        fetchHomes(boundsObj, filters);
        onBoundsChange(boundsObj);
      }
    }
  };

  useEffect(() => {
    if (map && !isPageLoaded) {
      const bounds = map.getBounds();
      if (bounds) {
        const boundsObj = {
          ne: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
          sw: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() }
        };
        fetchHomes(boundsObj, filters);
        onBoundsChange(boundsObj);
      }
      setIsPageLoaded(true);
    }
  }, [map, isPageLoaded]);

  const formatPrice = (price) => {
    return price ? `$${new Intl.NumberFormat().format(price)}` : 'Unavailable';
  };

  const getGradientColorForScore = (score) => {
    const green = [0, 255, 0];
    const red = [255, 0, 0];
    let ratio = score / 100;
    let color = [
      Math.round(green[0] * ratio + red[0] * (1 - ratio)),
      Math.round(green[1] * ratio + red[1] * (1 - ratio)),
      Math.round(green[2] * ratio + red[2] * (1 - ratio))
    ];
    return `rgb(${color.join(',')})`;
  };

  const svgMarker = (score, isSelected = false, isActive = false) => ({
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: isSelected ? '#0000FF' : getGradientColorForScore(score),
    fillOpacity: isSelected ? 0.9 : 0.6,
    strokeColor: isActive ? '#FFFFFF' : 'black',
    strokeWeight: isActive ? 3 : 1,
    scale: isActive ? 16 : isSelected ? 14 : 10,
  });

  return (
    <>
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center || defaultCenter}
          zoom={18}
          onIdle={handleIdle}
          onLoad={(map) => setMap(map)}
          onDragEnd={handleMapDragEnd}
          onZoomChanged={handleMapDragEnd}
          onClick={onMapClick}
        >
          <RenderCircle center={center || defaultCenter} radius={radius} />

          {tooManyHomes && (
            <Box
              position="absolute"
              bottom="30px"
              left="10px"
              style={{
                alignItems: 'center',
                display: 'flex',
                padding: '10px 15px',
                backgroundColor: '#ffffff',
                borderRadius: '5px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                zIndex: 10,
              }}
            >
              <WarningIcon style={{ marginRight: '5px' }} />
              <Typography variant="body2" style={{ color: 'black', fontSize: '14px' }}>
                Too many homes found. Please narrow down your search.
              </Typography>
            </Box>
          )}

          <MarkerClusterer
            minimumClusterSize={5}
            options={{
              imagePath:
                'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
            }}
          >
            {(clusterer) =>
              homes
                .filter((home) => home.id !== subjectProperty.id)
                .map((home) => {
                  const score = home.score;
                  const isSelected = selectedHomes.includes(home.id);
                  const isActive = selectedHome && selectedHome.id === home.id;

                  return (
                    <Marker
                      key={home.id}
                      position={{ lat: home.Latitude, lng: home.Longitude }}
                      onClick={() => setSelectedHome(home)}
                      icon={svgMarker(score, isSelected, isActive)}
                      clusterer={clusterer}
                    />
                  );
                })
            }
          </MarkerClusterer>

          <Marker
            key={subjectProperty.id}
            position={{ lat: subjectProperty.Latitude, lng: subjectProperty.Longitude }}
            icon="../src/assets/images/red-map-marker.png"
          />

          {selectedHome && (
            <InfoWindow
              position={{ lat: selectedHome.Latitude, lng: selectedHome.Longitude }}
              onCloseClick={() => setSelectedHome(null)}
            >
              <div>
                <img
                  src={`https://realtime-home-images.s3.amazonaws.com/${selectedHome.Ml_num}/1.jpeg`}
                  alt={getDisplayAddress(selectedHome)}
                  className="info-window-image"
                />
                <Typography variant="h6">{getDisplayAddress(selectedHome)}</Typography>
                <Typography variant="body2">Price: {formatPrice(selectedHome.Sp_dol)}</Typography>
                <Typography variant="body2">Bedrooms: {selectedHome.Br}</Typography>
                <Typography variant="body2">Bathrooms: {selectedHome.Bath_tot}</Typography>
                <Typography variant="body2">
                  Relevance Score:{' '}
                  {selectedHome.score !== undefined ? selectedHome.score.toFixed(2) : 'N/A'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={selectedHome.score || 0}
                  style={{ marginTop: 8, height: 6, borderRadius: 3 }}
                />
                <Button
                  variant="contained"
                  color={selectedHomes.includes(selectedHome.id) ? 'secondary' : 'primary'}
                  onClick={() => {
                    onConfirmClick(selectedHome); // this now handles toggling
                    setSelectedHome(null); // close InfoWindow
                  }}
                  style={{ marginTop: 10 }}
                >
                  {selectedHomes.includes(selectedHome.id)
                    ? 'Unselect this home'
                    : 'Select this home'}
                </Button>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </>
  );
};

export default Map;
